import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { CHAT_STATUS_PANEL_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the chat status panel gate (`/api/flags/chat-status-panel`, Conductor experiment
 * `symphonyChatStatusPanelWeb`). Same shape as `lib/chat-file-pane-flag.ts`: fails CLOSED to no panel, a
 * dev-tools override wins over the server, and only a real answer is remembered for the page load.
 */
const GATE_TIMEOUT_MS = 2500;

let cached: Promise<boolean> | null = null;
let known: boolean | null = null;

export function chatStatusPanelEnabled(): Promise<boolean> {
  const forced = toggleOverride(CHAT_STATUS_PANEL_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/chat-status-panel`, { credentials: 'include', signal: AbortSignal.timeout(GATE_TIMEOUT_MS) });
      if (res.ok) return (known = ((await res.json()) as boolean) === true);
    } catch {
      // closed for now; asked again by the next reader
    }
    cached = null;
    return false;
  })();
  return cached;
}

export function useChatStatusPanelEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => {
    const forced = toggleOverride(CHAT_STATUS_PANEL_SPEC);
    return forced !== undefined ? forced === 'true' : (known ?? false);
  });
  useEffect(() => {
    let live = true;
    void chatStatusPanelEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
